// @ignore i18n:lint
import React, { useContext, lazy } from 'react';
import { BrowserRouter as Router, Switch, Route, Redirect, useLocation, Link, NavLink } from 'react-router-dom';
import { ApolloProvider } from 'react-apollo-hooks';
import { loader } from 'graphql.macro';

import { ProfileContext, initApolloClient, RouterProvider } from '@loyalup/core';
import { ScrollToTop, GlobalError, ProfileLoader, CabinetLayout } from '@loyalup/ui';

import Clients from './Clients';
import ClientImport from './ClientImport';
import ClientOrders from './ClientOrders';
import Feedbacks from './Feedbacks';
import NewCampaign from './NewCampaign';
import Campaigns from './Campaigns';
import TriggerCampaigns from './TriggerCampaigns';
import DeliveryCardByEmail from './DeliveryCardByEmail';
import DeliveryCardBySMS from './DeliveryCardBySMS';
import Login from './Login';
import Register from './Register';
import Playground from './Playground';
import ForgotPassword from './ForgotPassword';
import UserProfile from './Settings/UserProfile';
import Company from './Settings/Company';
import Integrations from './Settings/Integrations';
import CheckPoster from './Settings/Integrations/CheckPoster';
import ResetPassword from './ResetPassword';
import ClientSignUpForm from './ClientSignUpForm';
import PassDesigner from './PassDesigner';
import TermsAndConditions from './TermsAndConditions';
import Loyalty from './Loyalty';
import Stats from './Stats';

import AcceptInvite from './AcceptInvite';
import ConfirmEmail from './ConfirmEmail';

import Agreement from './Agreement';

import SidebarMenu from '../components/SidebarMenu';

const ME_QUERY = loader('../gql/queries/Me.gql');

const GeoCampaigns = lazy(() => import('./GeoCampaigns'));

const isDev = process.env.NODE_ENV === 'development';

const CabinetRoute = ({ fullscreen, ...props }) => {
  const [, setFullScreen] = CabinetLayout.useFullScreenLayout();
  setFullScreen(Boolean(fullscreen));

  return <Route {...props} />;
};

const RedirectToHome = props => {
  const { hasPassDesign, permissions } = useContext(ProfileContext);
  const { manageCompanyCampaigns, manageCompanyClientOrders, manageCompanyClients, managePassDesigns } = permissions;

  let path = '/clients/list';

  const getFirstExistingRoute = () => {
    if (manageCompanyCampaigns) return '/campaigns/list';
    if (manageCompanyClientOrders) return '/orders';
  };

  if (!hasPassDesign) {
    path = '/designer';
  }

  if (!manageCompanyClients && !managePassDesigns) {
    path = getFirstExistingRoute() || path;
  }

  return <Redirect {...props} to={path} />;
};

const CabinetRouter = () => {
  const { permissions } = useContext(ProfileContext);

  const {
    companyAdmin,
    managePassDesigns,
    manageCompanyClients,
    manageCompanyCampaigns,
    manageCompanyClientOrders,
    manageCompanyLoyalties,
    viewCompanyStats,
  } = permissions;

  return (
    <CabinetLayout env={process.env.REACT_APP_ENV} sidebarMenu={<SidebarMenu />}>
      <Switch>
        <CabinetRoute exact path="/clients/imports/:sid" component={ClientImport} />
        {(companyAdmin || manageCompanyClients) && <CabinetRoute exact path="/clients/list" component={Clients} />}
        <CabinetRoute exact path="/clients/feedbacks" component={Feedbacks} />
        {(companyAdmin || manageCompanyClientOrders) && <CabinetRoute exact path="/orders" component={ClientOrders} />}
        <CabinetRoute exact path="/settings/me" component={UserProfile} />
        {companyAdmin && <CabinetRoute exact path="/settings/company" component={Company} />}
        <CabinetRoute exact path="/settings/integrations" component={Integrations} />
        <CabinetRoute path="/settings/integrations/poster/check" component={CheckPoster} />
        {(companyAdmin || manageCompanyCampaigns) && (
          <CabinetRoute exact path="/campaigns/list" component={Campaigns} />
        )}
        {(companyAdmin || manageCompanyCampaigns) && (
          <CabinetRoute exact path="/campaigns/triggers" component={TriggerCampaigns} />
        )}
        {(companyAdmin || manageCompanyCampaigns) && (
          <CabinetRoute exact path="/campaigns/new" component={NewCampaign} />
        )}
        {(companyAdmin || manageCompanyCampaigns) && (
          <CabinetRoute exact path="/campaigns/new/:id" component={NewCampaign} />
        )}
        {(companyAdmin || manageCompanyCampaigns) && (
          <CabinetRoute exact path="/campaigns/geo" component={GeoCampaigns} />
        )}
        <CabinetRoute exact path="/delivery/selfsignup" component={ClientSignUpForm} />
        <CabinetRoute exact path="/delivery/email" component={DeliveryCardByEmail} />
        <CabinetRoute exact path="/delivery/sms" component={DeliveryCardBySMS} />
        {(companyAdmin || managePassDesigns) && <CabinetRoute exact path="/designer" component={PassDesigner} />}
        {(companyAdmin || manageCompanyLoyalties) && (
          <CabinetRoute exact path="/loyalty/programs" component={Loyalty} />
        )}
        {(companyAdmin || viewCompanyStats) && <CabinetRoute exact path="/stats" component={Stats} />}
        <CabinetRoute fullscreen exact path="/user-agreement" component={TermsAndConditions} />
        <CabinetRoute fullscreen exact path="/agreement/:sid" component={Agreement} />
        <CabinetRoute fullscreen exact path="/confirm/:token" render={props => <ConfirmEmail {...props} />} />
        <CabinetRoute fullscreen exact path="/join/:token" component={AcceptInvite} />

        {isDev && <Route exact path="/playground" component={Playground} />}

        <Redirect from="/register" exact to="/" />
        <Redirect from="/login" exact to="/" />
        <RedirectToHome from="/" exact />
        <CabinetRoute fullscreen render={() => <GlobalError type="not-found" />} />
      </Switch>
    </CabinetLayout>
  );
};

const RouteWrapper = () => (
  <ApolloProvider client={initApolloClient(process.env.REACT_APP_GRAPHQL_URL, process.env.REACT_APP_GRAPHQL_WS)}>
    <ProfileLoader query={ME_QUERY}>
      <Routes />
    </ProfileLoader>
  </ApolloProvider>
);

const Routes = () => {
  const profile = useContext(ProfileContext);

  return (
    <Router
      getUserConfirmation={(message, callback) => {
        callback(message !== 'PREVENT_BACK');
      }}
    >
      <RouterProvider Link={Link} NavLink={NavLink} useLocation={useLocation}>
        <ScrollToTop />
        {profile ? (
          <CabinetRouter />
        ) : (
          <Switch>
            <Route exact path="/login" component={Login} />
            <Route exact path="/register" component={Register} />
            <Route exact path="/password/forgot" component={ForgotPassword} />
            <Route exact path="/password/reset/:token" component={ResetPassword} />

            <Route exact path="/user-agreement" component={TermsAndConditions} />
            <Route exact path="/agreement/:sid" component={Agreement} />
            <Route exact path="/join/:token" component={AcceptInvite} />
            <Route exact path="/confirm/:token" render={props => <ConfirmEmail {...props} />} />

            <Redirect from="/" to="/login" />
          </Switch>
        )}
      </RouterProvider>
    </Router>
  );
};

export default RouteWrapper;
